import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Card } from '@deckplan.io/common/cards';
import { CardViewComponent, CardViewDialogData } from '../card-view/card-view.component';
import { InventoryCard } from '@deckplan.io/common';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-card-grid',
    templateUrl: './card-grid.component.html',
    styleUrls: ['./card-grid.component.scss']
})
export class CardGridComponent implements OnInit {

    @Input() public items: any[] = [];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Input() set GridColumnCount(newColumnCount: number) {
        if (this.gridColumnCount === newColumnCount) {
            return;
        }
        this.gridColumnCount = newColumnCount;
        this.gridColumnsSet.emit(this.gridColumnCount);
    }

    @Input() public overrideClick = false;
    @Input() public removableCards: boolean | { deckId: number } = false;
    @Input() public addCardsToDeck: boolean | { deckId: number } = true;
    @Input() public addCardsToInventory = true;
    @Output() public cardActionTaken = new EventEmitter();

    @Output() public cardClicked = new EventEmitter<Card>();
    @Output() public cardContextClicked = new EventEmitter<{ event: MouseEvent; data: any }>();
    @Output() public gridColumnsSet = new EventEmitter<number>();

    // eslint-disable-next-line no-magic-numbers
    public readonly cardWidth = 125;

    // eslint-disable-next-line no-magic-numbers
    public gridColumnCount = 8;
    // eslint-disable-next-line no-magic-numbers
    private readonly maxColumnCount = 20;

    constructor(private readonly breakpointObserver: BreakpointObserver, private readonly dialog: MatDialog) {

        // Set up some breakpoints for us to use, based on the card width
        const breakpoints = [];
        for (let i = 1; i < this.maxColumnCount; i++) {
            breakpoints.push('(max-width: ' + i * this.cardWidth + 'px)');
        }

        // Modify the column count on the widget if the width of the viewport changes
        breakpointObserver.observe(breakpoints).subscribe((result: BreakpointState) => {
            let i = 0;
            for (const breakpointKey of Object.keys(result.breakpoints)) {
                // Locate the first one that is true, that will be the too big one.
                if (result.breakpoints[breakpointKey]) {
                    break;
                }
                i++;
            }

            this.GridColumnCount = i;
        });
    }

    @Input() public isValid = (card: Card) => true;

    public ngOnInit() {
        // Delay setting the column count a bit to let Angular catch up
        setTimeout(() => this.gridColumnsSet.emit(this.gridColumnCount));
    }

    /// Returns an array of cards given a row parameter according to the grid columns for this display
    public getCards(rowIndex: number): any[] {
        const startingPoint = rowIndex * this.gridColumnCount;
        this.items = this.items.filter(item => !item.temp);
        return this.items.slice(startingPoint, startingPoint + this.gridColumnCount);
    }

    public getTotalRows(): number {
        return Math.ceil(this.items.length / this.gridColumnCount);
    }

    /// Provides an array of the rows that we need to have on the display. Doing this because ngFor
    /// doesn't appear to allow us to just iterate from i to getTotalRows(), so this will provide
    /// us an array that contains that.
    public getRowsArray(): number[] {
        return Array(this.getTotalRows()).fill(0).map((x, i) => i);
    }

    public onCardClicked(card: Card | InventoryCard) {
        if (this.overrideClick) {
            this.cardClicked.emit(card);
        } else {
            const data = {
                card,
                removable: this.removableCards,
                addToDeck: this.addCardsToDeck,
                addToInventory: this.addCardsToInventory,
                quantity: (card as InventoryCard).quantity
            } as CardViewDialogData;
            const dialogRef = this.dialog.open(CardViewComponent, { data, minWidth: '600px' });
            dialogRef.componentInstance.actionTaken.subscribe(() => this.cardActionTaken.emit());
        }
    }

}
